<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  NCard,
  NUpload,
  NUploadDragger,
  NButton,
  NSpace,
  NPageHeader,
  NResult,
  NImage,
  NGrid,
  NGridItem,
  NModal,
  NPopconfirm,
  NSelect,
  NFormItem,
  NIcon,
  NEmpty,
  NDivider,
  NAlert,
  useMessage
} from 'naive-ui';
// ✅ Import Lucide icons
import { CloudUpload, Image as ImageIcon, Trash2 } from 'lucide-vue-next';

// ✅ Import your provider image service functions
import { 
  uploadProviderImage, 
  getProviderImages, 
  deleteProviderImage,
  getProvider
} from '@/services/provider';
import { authToken, isAuthenticated } from '@/services/auth';

const route = useRoute();
const router = useRouter();
const message = useMessage();

const providerId = computed(() => route.params.id);

// State variables
const loading = ref(false);
const uploading = ref(false);
const deleting = ref(false);
const error = ref(null);
const images = ref([]);
const providerData = ref(null);
const selectedImageType = ref('profile');

// For preview/delete modal
const showPreviewModal = ref(false);
const previewImage = ref(null);

// Image type options
const imageTypeOptions = [
  { label: 'Profile Picture', value: 'profile' },
  { label: 'Facility Photo', value: 'facility' },
  { label: 'Gallery', value: 'gallery' },
  { label: 'Certificate', value: 'certificate' }
];

// Check if we have a valid provider ID
const isValid = computed(() => !!providerId.value);

// ✅ Custom upload request using your service
const customRequest = async ({ file, onFinish, onError }) => {
  try {
    uploading.value = true;
    error.value = null;
    
    if (!isAuthenticated.value || !authToken.value) {
      throw new Error('Authentication required');
    }
    
    // Use your uploadProviderImage service
    const result = await uploadProviderImage(
      authToken.value,
      providerId.value,
      file.file,
      {
        image_type: selectedImageType.value,
        display_order: images.value.length // Auto-increment order
      }
    );
    
    if (!result.success) {
      throw new Error(result.error || 'Upload failed');
    }
    
    // Add the new image to our list
    images.value.push(result.image);
    
    message.success('Image uploaded successfully!');
    onFinish();
    
  } catch (err) {
    error.value = err.message || 'Failed to upload image';
    message.error(err.message || 'Failed to upload image');
    console.error('Upload error:', err);
    onError();
  } finally {
    uploading.value = false;
  }
};

// Load provider images on mount
onMounted(async () => {
  if (isValid.value) {
    await Promise.all([fetchProviderData(), fetchImages()]);
  }
});

// ✅ Fetch provider data using your service
const fetchProviderData = async () => {
  try {
    loading.value = true;
    error.value = null;
    
    if (!isAuthenticated.value || !authToken.value) {
      throw new Error('Authentication required');
    }
    
    const result = await getProvider(authToken.value, providerId.value);
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to load provider data');
    }
    
    providerData.value = result.provider;
  } catch (err) {
    error.value = 'Failed to load provider data';
    message.error(err.message || 'Failed to load provider data');
    console.error('Fetch provider error:', err);
  } finally {
    loading.value = false;
  }
};

// ✅ Fetch provider images using your service
const fetchImages = async () => {
  try {
    loading.value = true;
    error.value = null;
    
    const result = await getProviderImages(providerId.value);
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to load images');
    }
    
    images.value = result.images || [];
  } catch (err) {
    error.value = 'Failed to load provider images';
    message.error(err.message || 'Failed to load provider images');
    console.error('Fetch images error:', err);
  } finally {
    loading.value = false;
  }
};

// Handle image preview
const handlePreview = (image) => {
  previewImage.value = image;
  showPreviewModal.value = true;
};

// ✅ Delete an image using your service
const handleDeleteImage = async (imageId) => {
  try {
    deleting.value = true;
    error.value = null;
    
    if (!isAuthenticated.value || !authToken.value) {
      throw new Error('Authentication required');
    }
    
    const result = await deleteProviderImage(
      authToken.value,
      providerId.value,
      imageId
    );
    
    if (!result.success) {
      throw new Error(result.error || 'Delete failed');
    }
    
    // Remove the deleted image from our list
    images.value = images.value.filter(img => img.id !== imageId);
    
    // Close the modal if we're deleting the previewed image
    if (previewImage.value && previewImage.value.id === imageId) {
      showPreviewModal.value = false;
    }
    
    message.success('Image deleted successfully!');
    
  } catch (err) {
    error.value = err.message || 'Failed to delete image';
    message.error(err.message || 'Failed to delete image');
    console.error('Delete error:', err);
  } finally {
    deleting.value = false;
  }
};

// Get readable image type label
const getImageTypeLabel = (type) => {
  const option = imageTypeOptions.find(opt => opt.value === type);
  return option ? option.label : type;
};

// Navigate back
const handleBack = () => {
  router.back();
};
</script>

<template>
  <div class="provider-images-container">
    <NPageHeader 
      :title="providerData ? `Images for ${providerData.provider_name}` : 'Provider Images'"
      @back="handleBack"
    />
    
    <!-- Loading state -->
    <div v-if="loading && !error && !providerData" class="loading-container">
      <p>Loading...</p>
    </div>
    
    <!-- Error message -->
    <NResult 
      v-else-if="error && !providerData" 
      status="error" 
      title="Something Went Wrong" 
      :description="error"
    >
      <template #footer>
        <NButton @click="error = null; fetchImages(); fetchProviderData()">
          Try Again
        </NButton>
      </template>
    </NResult>
    
    <!-- Not found -->
    <NResult
      v-else-if="!isValid"
      status="404"
      title="Provider Not Found"
      description="Please select a valid provider to manage images."
    >
      <template #footer>
        <NButton @click="router.push('/my-providers')">
          Go to My Providers
        </NButton>
      </template>
    </NResult>
    
    <!-- Main content -->
    <div v-else class="provider-images-content">
      <!-- Upload section -->
      <NCard title="Upload New Image" class="upload-card">
        <NFormItem label="Image Type">
          <NSelect
            v-model:value="selectedImageType"
            :options="imageTypeOptions"
            placeholder="Select image type"
            size="large"
          />
        </NFormItem>
        
        <NUpload
          :custom-request="customRequest"
          :max="5"
          accept="image/*"
          :default-upload="false"
          list-type="image-card"
        >
          <NUploadDragger>
            <div class="upload-area">
              <!-- ✅ Lucide icon -->
              <NIcon size="48">
                <CloudUpload />
              </NIcon>
              <p>Click or drag files here to upload</p>
              <p class="upload-hint">
                Support for JPG, PNG, and WebP. <br />
                Maximum file size: 5MB
              </p>
            </div>
          </NUploadDragger>
        </NUpload>
        
        <NAlert v-if="uploading" type="info" style="margin-top: 1rem">
          Uploading image, please wait...
        </NAlert>
      </NCard>
      
      <!-- Gallery section -->
      <NCard title="Provider Images" class="gallery-card">
        <div v-if="images.length === 0" class="no-images">
          <NEmpty description="No images uploaded yet">
            <template #extra>
              <p>Upload your first image above</p>
            </template>
          </NEmpty>
        </div>
        
        <div v-else class="image-gallery">
          <NDivider>Image Gallery ({{ images.length }})</NDivider>
          
          <NGrid :cols="3" :x-gap="16" :y-gap="16" responsive="screen">
            <NGridItem v-for="image in images" :key="image.id" class="gallery-item">
              <div class="image-card">
                <div class="image-container" @click="handlePreview(image)">
                  <!-- ✅ Use presigned_url from backend -->
                  <img 
                    :src="image.presigned_url" 
                    :alt="`Provider ${image.image_type} image`" 
                    class="gallery-image" 
                  />
                </div>
                <div class="image-info">
                  <span class="image-type">{{ getImageTypeLabel(image.image_type) }}</span>
                  <NPopconfirm
                    @positive-click="handleDeleteImage(image.id)"
                    negative-text="Cancel"
                    positive-text="Delete"
                  >
                    <template #trigger>
                      <NButton circle tertiary type="error" size="small">
                        <template #icon>
                          <!-- ✅ Lucide icon -->
                          <NIcon>
                            <Trash2 />
                          </NIcon>
                        </template>
                      </NButton>
                    </template>
                    Are you sure you want to delete this image?
                  </NPopconfirm>
                </div>
              </div>
            </NGridItem>
          </NGrid>
        </div>
      </NCard>
    </div>
    
    <!-- Image preview modal -->
    <NModal
      v-model:show="showPreviewModal"
      preset="card"
      style="width: 600px; max-width: 90vw"
      title="Image Preview"
      :bordered="false"
      size="huge"
      :segmented="{ content: true, footer: 'soft' }"
    >
      <div v-if="previewImage" class="preview-container">
        <NImage
          :src="previewImage.presigned_url"
          object-fit="contain"
          :img-props="{
            style: {
              maxHeight: '60vh',
              width: '100%',
              objectFit: 'contain'
            }
          }"
        />
        
        <div class="preview-details">
          <p><strong>Type:</strong> {{ getImageTypeLabel(previewImage.image_type) }}</p>
          <p v-if="previewImage.description"><strong>Description:</strong> {{ previewImage.description }}</p>
          <p v-if="previewImage.created_at"><strong>Uploaded:</strong> {{ new Date(previewImage.created_at).toLocaleString() }}</p>
        </div>
      </div>
      
      <template #footer>
        <NSpace justify="end">
          <NButton @click="showPreviewModal = false">Close</NButton>
          <NPopconfirm
            v-if="previewImage"
            @positive-click="handleDeleteImage(previewImage.id)"
            negative-text="Cancel"
            positive-text="Delete"
          >
            <template #trigger>
              <NButton type="error" :loading="deleting">Delete Image</NButton>
            </template>
            Are you sure you want to delete this image?
          </NPopconfirm>
        </NSpace>
      </template>
    </NModal>
  </div>
</template>

<style scoped>
.provider-images-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.upload-card,
.gallery-card {
  margin-bottom: 2rem;
}

.upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: #666;
}

.upload-hint {
  font-size: 0.8rem;
  color: #999;
  margin-top: 0.5rem;
}

.image-gallery {
  margin-top: 1rem;
}

.gallery-item {
  min-width: 0; /* Important for responsive grid */
}

.image-card {
  border-radius: 8px;
  overflow: hidden;
  background: #f9f9f9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.image-card:hover {
  transform: translateY(-3px);
}

.image-container {
  height: 180px;
  overflow: hidden;
  cursor: pointer;
  background: #f0f0f0;
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.gallery-image:hover {
  transform: scale(1.05);
}

.image-info {
  padding: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.image-type {
  font-size: 0.85rem;
  color: #666;
  font-weight: 500;
}

.preview-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.preview-details {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}

.preview-details p {
  margin: 0.5rem 0;
  font-size: 0.9rem;
}

.no-images {
  padding: 2rem 0;
}
</style>